import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CollectionForm } from "@/components/collection/collection-form";
import { CollectionHeader } from "@/components/collection/collection-header";
import type { PickedProduct } from "@/components/collection/add-products-dialog";
import { PickedProducts } from "@/components/collection/picked-products";
import { Button } from "@/components/ui/button";
import GatedOverlay from "@/components/ui/gated-overlay";
import { useCreateCollection } from "@/hooks/collection";
import { ebayAccountsQueryOpts } from "@/hooks/ebay";
import { isGated } from "@/hooks/gated";
import { platformsQueryOpts } from "@/hooks/platform";
import { emptyCollectionInput, hasCollectionErrors } from "@/lib/collections";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";

export const Route = createFileRoute("/$org/collections/new")({
  loader: async ({ context }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);
    const params = { token: getToken() ?? "", setToken, orgId };

    const gated = await isGated(params, handleTokenInvalid);
    context.queryClient.ensureQueryData(platformsQueryOpts(params));
    // the marketplaces the collection can have eBay categories for
    context.queryClient.prefetchQuery(ebayAccountsQueryOpts(params));

    return { gated: gated?.is_gated };
  },
  component: NewCollectionComponent,
});

function NewCollectionComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const navigate = useNavigate();
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const createCollection = useCreateCollection();

  const [input, setInput] = useState(emptyCollectionInput);
  const [submitted, setSubmitted] = useState(false);
  // kept when the type changes, only manual collections save them
  const [products, setProducts] = useState<PickedProduct[]>([]);

  const { data: stores } = useQuery(
    platformsQueryOpts({ token: getToken() ?? "", setToken, orgId }),
  );

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  const save = () => {
    setSubmitted(true);
    if (hasCollectionErrors(input)) return;

    const productIds = products.map((product) => product.id);
    createCollection.mutate(
      { ...input, product_ids: productIds },
      {
        onSuccess: (collection) => {
          if (!collection) return;
          navigate({
            to: "/$org/collections/$id",
            params: { org, id: collection.id },
          });
        },
      },
    );
  };

  return (
    <div className="mx-auto flex max-w-4xl flex-col space-y-4">
      <CollectionHeader org={org} title={t("collections.create")}>
        <Button onClick={save} disabled={createCollection.isPending}>
          {createCollection.isPending
            ? t("collections.form.saving")
            : t("common.save")}
        </Button>
      </CollectionHeader>

      <CollectionForm
        value={input}
        onChange={setInput}
        isNew
        stores={stores ?? []}
        showErrors={submitted}
      />

      {input.type === "MANUAL" && (
        <PickedProducts products={products} onChange={setProducts} />
      )}
    </div>
  );
}
